/**
 * Pure logic for A/B redirect. Framework-agnostic so it can be unit tested
 * with Jest in Node and consumed by the Deno-based Netlify edge function.
 *
 * @typedef {Object} ABVariant
 * @property {string} id
 * @property {number} weight
 * @property {string} to
 *
 * @typedef {Object} ABTest
 * @property {string} id
 * @property {string} cookie
 * @property {number} ttlDays
 * @property {ABVariant[]} variants
 *
 * @typedef {Record<string, ABTest>} ABTests
 *
 * @typedef {Object} CookieSetOptions
 * @property {string} name
 * @property {string} value
 * @property {string} [path]
 * @property {number} [maxAge]
 * @property {"Strict"|"Lax"|"None"} [sameSite]
 * @property {boolean} [secure]
 */

/**
 * Pick a variant id deterministically given a random function returning [0,1).
 * @param {ABVariant[]} variants
 * @param {() => number} [randomFn]
 * @returns {string}
 */
export function pickVariant(variants, randomFn = Math.random) {
  const total = variants.reduce((sum, v) => sum + v.weight, 0);
  let r = randomFn() * total;
  for (const v of variants) {
    r -= v.weight;
    if (r < 0) return v.id;
  }
  return variants[variants.length - 1].id;
}

/**
 * Resolve to a variant id. If the existing cookie value matches a known variant,
 * keep it sticky. Otherwise pick a new one.
 * @param {ABTest} test
 * @param {string|null|undefined} existingCookieValue
 * @param {() => number} [randomFn]
 * @returns {{ variantId: string, isNew: boolean }}
 */
export function resolveVariant(test, existingCookieValue, randomFn = Math.random) {
  const known = existingCookieValue
    ? test.variants.find((v) => v.id === existingCookieValue)
    : undefined;
  if (known) return { variantId: known.id, isNew: false };
  return { variantId: pickVariant(test.variants, randomFn), isNew: true };
}

/**
 * Build the redirect destination preserving query and hash from the source URL.
 * @param {string} variantTo
 * @param {string|undefined} search
 * @param {string|undefined} hash
 * @returns {string}
 */
export function buildDestinationUrl(variantTo, search, hash) {
  return `${variantTo}${search || ""}${hash || ""}`;
}

/**
 * Collect all variant destination URLs from the A/B test config.
 * Used at build time (gatsby-node) to decide which landing pages should be
 * served with a noindex meta tag while a test is running.
 *
 * @param {ABTests} abTests
 * @returns {Set<string>}
 */
export function getVariantPaths(abTests) {
  const paths = new Set();
  for (const test of Object.values(abTests)) {
    for (const variant of test.variants) {
      paths.add(variant.to);
    }
  }
  return paths;
}

/**
 * Normalize a page path to match how variant `to` values are stored in config.
 * Variant URLs in ab-tests.json always use a trailing slash; Gatsby's
 * createPage path may omit it, so we add one when missing.
 *
 * @param {string} path
 * @returns {string}
 */
export function normalizePath(path) {
  if (!path) return path;
  return path.endsWith("/") ? path : `${path}/`;
}

/**
 * Entry point used by the edge function. Pure - all side effects are funneled
 * through the injected getCookie/setCookie callbacks so it is straightforward
 * to unit test.
 *
 * Lookup is case-insensitive and trailing-slash-insensitive: requests for
 * `/Kurs/Rysunek-A` and `/kurs/rysunek-a/` both match a config key of
 * `/kurs/rysunek-a/`. Prevents the URL-casing/trailing-slash bypass where
 * Netlify would otherwise fall through to the static page.
 *
 * @param {Object} args
 * @param {string} args.pathname
 * @param {string} [args.search]
 * @param {string} [args.hash]
 * @param {(name: string) => string|null|undefined} args.getCookie
 * @param {(opts: CookieSetOptions) => void} args.setCookie
 * @param {ABTests} args.abTests
 * @param {() => number} [args.randomFn]
 * @returns {{ status: 302, location: string, variantId: string, isNew: boolean } | null}
 */
export function handleAbRequest({
  pathname,
  search,
  hash,
  getCookie,
  setCookie,
  abTests,
  randomFn = Math.random,
}) {
  const normalized = normalizePath(pathname).toLowerCase();
  const test = Object.entries(abTests).find(
    ([key]) => normalizePath(key).toLowerCase() === normalized
  )?.[1];
  if (!test) return null;

  const existing = getCookie(test.cookie);
  const { variantId, isNew } = resolveVariant(test, existing, randomFn);

  if (isNew) {
    setCookie({
      name: test.cookie,
      value: variantId,
      path: "/",
      maxAge: test.ttlDays * 86400,
      sameSite: "Lax",
      secure: true,
    });
  }

  const variant = test.variants.find((v) => v.id === variantId);
  return {
    status: 302,
    location: buildDestinationUrl(variant.to, search, hash),
    variantId,
    isNew,
  };
}
